import { Theme } from '@/components/Block'
import { ButtonTheme } from '@/components/Button'
import IcoMoon from '@/components/IcoMoon'
import { UnitSize } from '@/constants/measured-scope'
import { SectionNavigationAction } from '@/shared/api-generated-types'
import { useMediaQuery } from '@react-hookz/web'
import * as React from 'react'
import { useMemo, useRef } from 'react'
import RSSlider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import * as styles from './style'

type CarouselOptions = false | UnitSize[] | 'always'

export interface CardRowProps {
  title?: string | null
  subtitle?: string | null
  theme?: Theme
  carousel?: CarouselOptions
  spacing?: UnitSize
  className?: string
  navigationActions?: SectionNavigationAction[]
  children: React.ReactNode[]
  instructorCard?: boolean
  categoryCard?: boolean
}

const buttonThemeMap: { [x: string]: ButtonTheme } = {
  primary: 'white',
  secondary: 'glass',
  tertiary: 'white',
  white: 'primaryOutline',
}

const getTextColorClass = (theme: string): string => {
  switch (buttonThemeMap[theme]) {
    case 'white':
      return 'text-white'
    case 'glass':
      return 'text-gray-500'
    case 'primaryOutline':
      return 'text-primary'
    default:
      return ''
  }
}

export type DeviceSizes = {
  [key in UnitSize]: boolean | undefined
}

const CardRow = ({
  title,
  subtitle,
  theme = 'white',
  navigationActions,
  children,
  instructorCard = false,
  categoryCard = false,
  className = '',
}: CardRowProps): JSX.Element => {
  const rsSliderRef = useRef<any>(null)
  const isSmallDevice = useMediaQuery(`only screen and (min-width : 0)`, { initializeWithValue: false })

  const carouselArrows = useMemo(() => {
    const prev = (): void => {
      if (rsSliderRef.current && rsSliderRef.current.slickPrev) {
        rsSliderRef.current.slickPrev()
      }
    }
    const next = (): void => {
      if (rsSliderRef.current && rsSliderRef.current.slickNext) {
        rsSliderRef.current.slickNext()
      }
    }
    return (
      <div className="flex items-center navArrows">
        {!!navigationActions && isSmallDevice && (
          <>
            {navigationActions.map((item) => {
              return (
                <div key={item.label} className={`text-center lg:mr-2 font-semibold ${getTextColorClass(theme)}`}>
                  <button>{item.label}</button>
                </div>
              )
            })}
          </>
        )}
        <a className={`${styles.rowArrow} mr-2`} onClick={prev}>
          <IcoMoon icon="chevron-left" />
        </a>
        <a className={styles.rowArrow} onClick={next}>
          <IcoMoon icon="chevron-right" />
        </a>
      </div>
    )
  }, [isSmallDevice])

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 6,
    variableWidth: false,
    arrows: false,
    responsive: [
      instructorCard || categoryCard
        ? {
            //Below 4000
            breakpoint: 4000,
            settings: {
              slidesToShow: 6,
              slidesToScroll: 6,
            },
          }
        : {
            breakpoint: 4000,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          },

      instructorCard || categoryCard
        ? {
            //Below 1800
            breakpoint: 1880,
            settings: {
              slidesToShow: 6,
              slidesToScroll: 6,
            },
          }
        : {
            breakpoint: 1880,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          },

      instructorCard || categoryCard
        ? {
            //Below 1600
            breakpoint: 1600,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          }
        : {
            breakpoint: 1600,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 4,
            },
          },
      instructorCard || categoryCard
        ? {
            //Below 1440
            breakpoint: 1440,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          }
        : {
            breakpoint: 1440,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          },

      instructorCard || categoryCard
        ? {
            //Below 1280
            breakpoint: 1280,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 4,
            },
          }
        : {
            breakpoint: 1280,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 4,
            },
          },
      instructorCard || categoryCard
        ? {
            //Below 980
            breakpoint: 980,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 3,
            },
          }
        : {
            breakpoint: 980,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 3,
            },
          },
      instructorCard || categoryCard
        ? {
            //Below 600
            breakpoint: 600,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 3,
            },
          }
        : {
            breakpoint: 600,
            settings: {
              slidesToShow: 2.1,
              slidesToScroll: 2,
            },
          },
      instructorCard || categoryCard
        ? {
            //Below 450
            breakpoint: 450,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2,
            },
          }
        : {
            breakpoint: 450,
            settings: {
              slidesToShow: 1.2,
              slidesToScroll: 1,
            },
          },
    ],
  }

  return (
    <section className={`${styles.root} py-3 md:py-6 ${className}`}>
      <div className="w-full">
        <div className="flex justify-between items-center mb-2">
          {title ? <h2 className={styles.heading}>{title}</h2> : <div></div>}
          {carouselArrows}
        </div>
        {subtitle && <p className="text-xs sm:text-sm xxl:test-base">{subtitle}</p>}
        <RSSlider {...sliderSettings} ref={rsSliderRef} className="px-0 pb-2">
          {children.map((card, key) => (
            <div key={key}>{card}</div>
          ))}
        </RSSlider>
      </div>
    </section>
  )
}

export default CardRow
